import { useState, type FormEvent } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useAuth } from '@/auth/AuthProvider';
import {
  EMAIL_INVALID_MESSAGE,
  isValidEmail,
  safeRedirect,
  signInArgs,
  signInFailureMessage,
  signInNotice,
  type SignInKind,
} from '@/auth/signInModel';
import { authorityHost, config } from '@/config';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { AppMark, Logo } from '@/components/Logo';
import { Dot, Tag } from '@/components/badges';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

type Busy = SignInKind | null;

/** Starts the OIDC redirect; `busy` is the button that was pressed, `error` the failure to reach the IdP. */
function useStartSignIn(redirect: string, email: string) {
  const { signIn } = useAuth();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async (kind: SignInKind) => {
    setError(null);
    setBusy(kind);
    try {
      await signIn(signInArgs(redirect, kind, email));
    } catch (e) {
      setBusy(null);
      setError(signInFailureMessage(authorityHost(), e));
    }
  };
  return { busy, error, start };
}

/** The email field and its validation; submitting a valid address starts the redirect with it as login_hint. */
function useSignInFlow(redirect: string) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const { busy, error, start } = useStartSignIn(redirect, email);

  const onEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_INVALID_MESSAGE);
      return;
    }
    setEmailError(undefined);
    void start('email');
  };
  const onEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError(undefined);
  };
  return { email, emailError, busy, error, start, onEmailSubmit, onEmailChange };
}

type SignInFlow = ReturnType<typeof useSignInFlow>;

function SignInHeader() {
  return (
    <header className="flex h-16 shrink-0 items-center justify-between px-5 sm:px-6">
      <Logo height={24} />
      <Tag title="OIDC authority">issuer {authorityHost()}</Tag>
    </header>
  );
}

function SignInHero() {
  return (
    <div className="flex flex-col items-center text-center">
      <AppMark size={64} className="shadow-lift" />
      <div className="mt-4 flex items-center gap-2">
        <span className="text-2xl font-semibold tracking-tight text-foreground">hangar</span>
        <Tag>v{config.version}</Tag>
      </div>
      <h1 id="signin-title" className="mt-2 text-[22px] font-semibold tracking-tight text-foreground">
        Sign in to Hangar
      </h1>
      <p className="mt-1.5 max-w-[36ch] text-sm text-muted-foreground">Build and deliver your mobile apps.</p>
    </div>
  );
}

function SignInAlerts({ notice, error }: { notice: string | null; error: string | null }) {
  return (
    <>
      {notice ? (
        <Alert className="mt-5 px-3 py-2">
          <AlertDescription className="text-[13px]">{notice}</AlertDescription>
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="destructive" className="mt-5 px-3 py-2">
          <AlertDescription className="text-[13px]">{error}</AlertDescription>
        </Alert>
      ) : null}
    </>
  );
}

function GoogleMark() {
  return (
    <span aria-hidden className="grid size-5 shrink-0 place-items-center rounded-full bg-primary-foreground text-[12px] font-bold leading-none text-primary">
      G
    </span>
  );
}

function GoogleButton({ busy, onClick }: { busy: Busy; onClick: () => void }) {
  return (
    <Button data-testid="signin-button" size="lg" className="mt-7 h-auto min-h-11 w-full whitespace-normal py-2.5" onClick={onClick} loading={busy === 'google'} disabled={busy !== null}>
      {busy === 'google' ? null : <GoogleMark />}
      {/* The primary label is never truncated (premium-ui §2.7): on a narrow phone it wraps onto two balanced lines
          and the button grows. The domain hint lives under the button instead of inside it. */}
      <span className="min-w-0 text-balance">Continue with Google Workspace</span>
    </Button>
  );
}

function ClientDivider() {
  return (
    <div className="my-6 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.12em] text-subtle">
      <Separator className="flex-1" />
      Client or partner?
      <Separator className="flex-1" />
    </div>
  );
}

function MfaHint() {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-subtle">
      <Dot tone="awaiting" />
      MFA required for guests
    </span>
  );
}

function EmailForm({ flow }: { flow: SignInFlow }) {
  const { email, emailError, busy, onEmailSubmit, onEmailChange } = flow;
  return (
    <form onSubmit={onEmailSubmit} noValidate className="flex flex-col gap-4">
      <FormField id="signin-email" label="Email address" error={emailError} trailing={<MfaHint />}>
        <Input
          data-testid="signin-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@yourcompany.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          className="h-11"
          {...fieldA11y('signin-email', emailError)}
        />
      </FormField>
      <Button type="submit" variant="outline" data-testid="signin-email-submit" size="lg" className="w-full" loading={busy === 'email'} disabled={busy !== null}>
        Continue with email
        <Icon name="arrow_forward" size={18} />
      </Button>
    </form>
  );
}

function SignInFooter() {
  return (
    <footer className="flex h-12 shrink-0 items-center justify-center gap-2 px-6 font-mono text-[11px] text-subtle">
      <span>Hangar by Malible</span>
      <span aria-hidden>·</span>
      <span>Build and deliver your mobile apps</span>
    </footer>
  );
}

/**
 * Screen 25. Both actions start the same OIDC redirect (the IdP behind the authority decides the method); the email
 * path passes the address as login_hint. The mockup's security badges and MFA copy are not claims this console can
 * make, so they are left out.
 */
export function SignIn() {
  const search = useSearch({ strict: false }) as { redirect?: string; reason?: string };
  const flow = useSignInFlow(safeRedirect(search.redirect));
  return (
    <div className="blueprint flex min-h-dvh flex-col bg-background">
      <SignInHeader />
      <main className="flex flex-1 items-start justify-center px-4 pb-10 pt-4 sm:items-center sm:pb-16 sm:pt-0">
        <Card aria-labelledby="signin-title" className="fade-in relative w-full max-w-[440px] overflow-hidden rounded-xl p-6 shadow-overlay sm:p-8">
          <div aria-hidden className="absolute inset-x-10 top-0 h-px bg-primary/80" />
          <SignInHero />
          <SignInAlerts notice={signInNotice(search.reason)} error={flow.error} />
          <GoogleButton busy={flow.busy} onClick={() => void flow.start('google')} />
          <p className="mt-2 text-center font-mono text-[11px] text-muted-foreground">malible.com accounts</p>
          <ClientDivider />
          <EmailForm flow={flow} />
        </Card>
      </main>
      <SignInFooter />
    </div>
  );
}
